<template>
  <div class="container app-shell" :class="{ 'is-landing': isLanding }">
    <app-header>
      <section class="header-section">
        <router-link class="brand-link" to="/home">
          <app-brand-logo />
        </router-link>
        <section>
          <nav v-if="isLanding && !isMobile" class="landing-nav">
            <router-link to="/consultation">{{ t('consultation') }}</router-link>
            <router-link to="/FAQ">{{ t('FAQ') }}</router-link>
            <router-link to="/contacts">{{ t('contacts') }}</router-link>
            <router-link to="/signIn">{{ t('login') }}</router-link>
            <router-link class="nav-cta" to="/signUp">{{ t('signUp') }}</router-link>
          </nav>
          <app-dropdown v-model="language" label="name">
            <app-option v-for="item in languages" :key="item" :value="item">{{ item }} </app-option>
          </app-dropdown>
          <app-burger-menu v-if="isMobile" :landing="isLanding"></app-burger-menu>
        </section>
      </section>
    </app-header>

    <section class="open-layout app-shell__body">
      <app-sidebar v-if="!isLanding || isMobile" :landing="isLanding">
        <template #main="{ isShort }">
          <router-link to="/home" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'home']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('home') }}</div>
            </div>
          </router-link>
          <router-link to="/consultation" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'user-tie']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('consultation') }}</div>
            </div>
          </router-link>
          <router-link to="/FAQ" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'question-circle']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('FAQ') }}</div>
            </div>
          </router-link>
          <router-link to="/contacts" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'contact-card']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('contacts') }}</div>
            </div>
          </router-link>
          <router-link to="/signIn" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'sign-in']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('login') }}</div>
            </div>
          </router-link>

          <router-link to="/signUp" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'user-plus']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('signUp') }}</div>
            </div>
          </router-link>

          <router-link to="/account/delete" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'trash']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('accountDeleteTitle') }}</div>
            </div>
          </router-link>

          <!-- Static page outside the SPA, so it is a plain link and not a router-link. -->
          <a :href="privacyUrl" target="_blank" rel="noopener">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'shield-halved']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('privacyPolicy') }}</div>
            </div>
          </a>
          <a :href="termsUrl" target="_blank" rel="noopener">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'file-lines']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('termsOfService') }}</div>
            </div>
          </a>
        </template>

        <template #additional="{ isShort, toggle }">
          <div v-if="!isMobile" class="sidebar-item" @click="toggle">
            <font-awesome-icon
              size="xl"
              :icon="isShort ? ['fas', 'arrow-left'] : ['fas', 'arrow-right']"
            />
          </div>
        </template>
      </app-sidebar>
      <main class="main app-shell__main">
        <router-view />
      </main>
    </section>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useStore } from 'vuex';
import AppSidebar from '@/shared/components/AppSidebar.vue';
import AppOption from '@/shared/components/dropdown/AppOption.vue';
import AppDropdown from '@/shared/components/dropdown/AppDropdown.vue';
import AppHeader from '@/shared/components/AppHeader.vue';
import { useI18n } from 'vue-i18n';
import AppBurgerMenu from '@/shared/components/AppBurgerMenu.vue';
import AppBrandLogo from '@/shared/components/AppBrandLogo.vue';
import { privacyPolicyUrl, termsOfServiceUrl } from '@/helpers/privacy-url.helper';

export default defineComponent({
  components: {
    AppBrandLogo,
    AppBurgerMenu,
    AppSidebar,
    AppOption,
    AppDropdown,
    AppHeader,
  },
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const route = useRoute();
    const languages = ref(['en', 'ru']);
    const language = ref(store.getters['language/language']);
    const isMobile = computed(() => store.getters['mobile/breakPoint'] === 'mobile');
    const isLanding = computed(
      () =>
        route.name === 'home' ||
        route.name === 'signIn' ||
        route.name === 'signUp' ||
        route.name === 'contacts' ||
        route.name === 'FAQ' ||
        route.name === 'forgot' ||
        route.name === 'open-consultation' ||
        route.name === 'account-delete'
    );
    const privacyUrl = computed(() => privacyPolicyUrl(language.value));
    const termsUrl = computed(() => termsOfServiceUrl(language.value));

    watch(language, (newValue) => {
      store.dispatch('language/setLanguage', { language: newValue });
    });

    return {
      language,
      languages,
      isMobile,
      isLanding,
      privacyUrl,
      termsUrl,
      t,
    };
  },
});
</script>
<style lang="scss">
.container {
  & .header-section {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & section {
      display: flex;
      align-items: center;
      gap: 14px;
    }
  }

  & .open-layout {
    & .main {
      width: 100%;
    }
  }
}

.brand-link {
  display: flex;
  align-items: center;
  line-height: 0;
}

.landing-nav {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-right: 8px;

  a {
    font-size: 13px;
    letter-spacing: 0.04em;
    color: rgba(246, 243, 238, 0.78);
    cursor: pointer;
  }

  a:hover,
  a.router-link-active {
    color: #f6f3ee;
  }

  .nav-cta {
    padding: 8px 14px;
    border-radius: 999px;
    color: #141018;
    background: #f6f3ee;
    transition: transform 0.3s ease, background 0.3s ease;
  }

  .nav-cta:hover,
  .nav-cta.router-link-active {
    color: #141018;
    background: #fff;
    transform: translateY(-1px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing-nav .nav-cta {
    transition: none;
  }

  .landing-nav .nav-cta:hover,
  .landing-nav .nav-cta.router-link-active {
    transform: none;
  }
}

.container.is-landing {
  background: #141018;

  .header {
    flex-shrink: 0;
    position: relative;
    z-index: 30;
    height: 56px;
    padding: 8px 24px;
    background: rgba(20, 16, 24, 0.72);
    border-bottom: 1px solid rgba(246, 243, 238, 0.08);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }

  .app-shell__main {
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.dark .landing-nav a {
  color: rgba(246, 243, 238, 0.75);
}

.dark .landing-nav .nav-cta,
.dark .landing-nav .nav-cta:hover,
.dark .landing-nav .nav-cta.router-link-active {
  color: #141018;
}
</style>
